import { merge } from "lodash";

import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";

import {
  HandBookTemplateViewFilters,
  ICreateManualExpenditure,
  ICreateTemplateExpenditure,
  IHandBook,
  IHandBookAggregate,
  IHandBookSegment,
  IHandBookTemplate,
  IHandBookTemplateExpenditure,
  IHandBookTemplateSection,
} from "./types";
import { IListResponseData } from "types/ListResponse";

export interface HandBookLocalEditings<T> {
  /** Удалённые */
  deletions: Record<number, boolean>;
  /** Отредактированные */
  changes: Record<number, Partial<T>>;
  /** Отмеченные чекбоксом */
  checked: Record<number, boolean>;
  /** Новые добавляемые */
  new: Record<string, any>[];
  /** Скопированные */
  clones: Record<number, T[]>;
}

export interface HandbookExpenditureLocalChanges {
  expenditure?: {
    /** id расценки по Справочнику (выбор через автокомплит) или данные для создания новой расценки в Справочнике */
    handbook?: ICreateManualExpenditure | number;
    /** Данные расценки в шаблоне – цена, кол-во, атрибуты */
    template?: Omit<ICreateTemplateExpenditure, "manual_expenditure_id">;
  };
  newMaterials?: Array<{
    /** id ресурса по Справочнику (выбор через автокомплит) или данные для создания нового ресурса в Справочнике */
    handbook?: ICreateManualExpenditure | number;
    /** Данные ресурса в шаблоне – цена, кол-во, атрибуты */
    template: Omit<ICreateTemplateExpenditure, "manual_expenditure_id" | "related_work_id">;
  }>;
  editedMaterials?: Array<{
    /** id ресурса по Справочнику (выбор через автокомплит) или данные для создания нового ресурса в Справочнике */
    handbook?: ICreateManualExpenditure | number;
    /** Данные ресурса в шаблоне – цена, кол-во, атрибуты */
    template: IHandBookTemplateExpenditure & { manual_expenditure: { id: number } };
  }>;
  /** Список id ресурсов для удаления из шаблона */
  deletedMaterials?: number[];
}

export enum HANDBOOK_TEMPLATE_LVL {
  TEMPLATES = "templates",
  SECTIONS = "sections",
  EXPENDITURES = "expenditures",
  SEGMENTS = "segments",
}

interface HandBookState {
  invalidationKey: number;
  loadings: Record<string, boolean>;
  handbooks: Record<string, IListResponseData<IHandBook>>;
  handbookAggregate: IHandBookAggregate;
  /** Чтобы добавить строку в handbooks ^^ нужно пробрасывать key через интерфейс (непонятно зачем), поэтому добавлять будем в последний использованный для загрузки строк key  */
  _lastUsedKey: string;
  editings: { isEditing: boolean } & HandBookLocalEditings<IHandBook>;
  templateEditings: {
    isEditing: boolean;
    [HANDBOOK_TEMPLATE_LVL.TEMPLATES]: HandBookLocalEditings<IHandBookTemplate>;
    [HANDBOOK_TEMPLATE_LVL.SECTIONS]: HandBookLocalEditings<IHandBookTemplateSection>;
    [HANDBOOK_TEMPLATE_LVL.EXPENDITURES]: HandBookLocalEditings<HandbookExpenditureLocalChanges>;
    [HANDBOOK_TEMPLATE_LVL.SEGMENTS]: HandBookLocalEditings<IHandBookSegment>;
  };
  templates: Record<string, IListResponseData<IHandBookTemplate>>;
  templateSections: Record<string, IListResponseData<IHandBookTemplateSection>>;
  templateExpenditures: Record<string, IListResponseData<IHandBookTemplateExpenditure>>;
  _lastUsedKeyTemplateExpenditures: string;
  /** Сегменты для навигации в Шаблонах */
  segments: IHandBookSegment[];
  /** Все доступные в Справочнике Атрибуты */
  allSegments: IHandBookSegment[];
  templateViewFilters: HandBookTemplateViewFilters;
}

const initEditing = () => ({
  deletions: {},
  changes: {},
  checked: {},
  clones: {},
  new: [],
});

const initTemplateEditings = () => ({
  isEditing: false,
  templates: initEditing(),
  sections: initEditing(),
  expenditures: initEditing(),
  segments: initEditing(),
});

export const initTemplateViewFilters = () => ({
  attribute: false,
  material: true,
  machine: true,
  transport: true,
  equipment: true,
});

export const createHandbookInitialState = (): HandBookState => ({
  invalidationKey: 0,
  loadings: {},
  handbooks: {},
  handbookAggregate: {
    in_review_count: 0,
  },
  _lastUsedKey: "",
  editings: {
    isEditing: false,
    ...initEditing(),
  },
  templateEditings: initTemplateEditings(),
  templates: {},
  templateSections: {},
  templateExpenditures: {},
  _lastUsedKeyTemplateExpenditures: "",
  segments: [],
  allSegments: [],
  templateViewFilters: initTemplateViewFilters(),
});

const initialState: HandBookState = createHandbookInitialState();

const handBookSlice = createSlice({
  name: "handBook",
  initialState,
  reducers: {
    invalidateKey: (state) => {
      state.invalidationKey++;
    },
    setIsLoading: (state, action: PayloadAction<{ status: boolean; key: string }>) => {
      const { status, key } = action.payload;
      state.loadings[key] = status;
    },
    setEditing: (state, action: PayloadAction<boolean>) => {
      state.editings.isEditing = action.payload;
    },
    setEditingTemplates: (state, action: PayloadAction<boolean>) => {
      state.templateEditings.isEditing = action.payload;
    },
    setItems: (state, action: PayloadAction<{ items: IListResponseData<IHandBook>; key: string }>) => {
      const { items, key } = action.payload;
      state.handbooks[key] = items;
      state._lastUsedKey = key;
    },
    setItemsMore: (state, action: PayloadAction<{ items: IListResponseData<IHandBook>; key: string }>) => {
      const { items, key } = action.payload;
      state.handbooks[key] = { ...state.handbooks[key], results: [...state.handbooks[key].results, ...items.results] };
      state._lastUsedKey = key;
    },
    updateItem: (state, action: PayloadAction<IHandBook>) => {
      state.handbooks = Object.fromEntries(
        Object.entries(state.handbooks).map(([key, obj]) => [
          key,
          {
            ...obj,
            results: obj.results.map((el) => {
              // @ts-ignore
              if (el.id === action.payload.id || el.id === action.payload._id) {
                return action.payload;
              }
              return el;
            }),
          },
        ])
      );
    },
    deleteItem: (state, action: PayloadAction<number>) => {
      state.handbooks = Object.fromEntries(
        Object.entries(state.handbooks).map(([key, obj]) => [
          key,
          {
            ...obj,
            results: obj.results.filter((el) => {
              if (el.id === action.payload) {
                return false;
              }
              return true;
            }),
          },
        ])
      );
    },
    addItem: (state, action: PayloadAction<{ item: IHandBook; key: string }>) => {
      state.handbooks[action.payload.key] = {
        ...state.handbooks[action.payload.key],
        results: (state.handbooks[action.payload.key].results || [])?.concat(action.payload.item),
      };
    },
    clearEditings: (state) => {
      state.editings = {
        isEditing: false,
        deletions: {},
        changes: {},
        checked: {},
        clones: {},
        new: [],
      };
    },
    localDeleteItem: (state, action: PayloadAction<number>) => {
      state.editings.deletions[action.payload] = true;
    },
    localChangeItem: (state, action: PayloadAction<{ id: number; change: Partial<IHandBook> }>) => {
      state.editings.changes[action.payload.id] = Object.assign(
        {},
        state.editings.changes[action.payload.id] || {},
        action.payload.change
      );
    },
    localCheckItem: (state, action: PayloadAction<{ id: number; isChecked: boolean }>) => {
      state.editings.checked[action.payload.id] = action.payload.isChecked;
    },
    localReplaceChecked: (state, action: PayloadAction<Record<number, boolean>>) => {
      state.editings.checked = action.payload;
    },
    localAddItem: (state, action: PayloadAction<{ id: number; _new: true }>) => {
      state.editings.new = state.editings.new.concat(action.payload);
    },
    localDeleteAddingItem: (state, action: PayloadAction<number>) => {
      state.editings.new = state.editings.new.filter((x) => x.id !== action.payload);
    },
    clearLocalAddings: (state) => {
      state.editings.new = [];
    },
    localAddTemplateItem: (
      state,
      action: PayloadAction<{
        item: (IHandBookTemplateExpenditure | IHandBookTemplateSection | IHandBookTemplate) & { _originalId?: number };
        lvl: HANDBOOK_TEMPLATE_LVL;
      }>
    ) => {
      const { item, lvl } = action.payload;
      state.templateEditings[lvl].new = state.templateEditings[lvl].new.concat(item as any);
    },
    localDeleteTemplateAddingItem: (state, action: PayloadAction<{ id: number; lvl: HANDBOOK_TEMPLATE_LVL }>) => {
      const { id, lvl } = action.payload;
      state.templateEditings[lvl].new = state.templateEditings[lvl].new.filter((x) => x.id !== id);
    },
    clearLocalTemplateAddings: (state, action: PayloadAction<{ lvl: HANDBOOK_TEMPLATE_LVL }>) => {
      state.templateEditings[action.payload.lvl].new = [];
    },
    clearTemplateEditings: (state) => {
      state.templateEditings = initTemplateEditings();
    },
    localDeleteTemplateItem: (state, action: PayloadAction<{ id: number; lvl: HANDBOOK_TEMPLATE_LVL }>) => {
      const { id, lvl } = action.payload;
      state.templateEditings[lvl].deletions[id] = true;
    },
    localBulkDeleteTemplateItem: (state, action: PayloadAction<{ lvl: HANDBOOK_TEMPLATE_LVL; ids: number[] }>) => {
      action.payload.ids.forEach((id) => {
        state.templateEditings[action.payload.lvl].deletions[id] = true;
      });
    },
    localChangeTemplateItem: (
      state,
      action: PayloadAction<{ id: number; change: object; lvl: HANDBOOK_TEMPLATE_LVL }>
    ) => {
      const { id, change, lvl } = action.payload;
      state.templateEditings[lvl].changes[id] = merge({}, state.templateEditings[lvl].changes[id] || {}, change);
    },
    localCheckTemplateItem: (
      state,
      action: PayloadAction<{ id: number; isChecked: boolean; lvl: HANDBOOK_TEMPLATE_LVL }>
    ) => {
      const { id, isChecked, lvl } = action.payload;
      state.templateEditings[lvl].checked[id] = isChecked;
    },
    setTemplates: (
      state,
      { payload }: PayloadAction<{ key: string; templates: IListResponseData<IHandBookTemplate> }>
    ) => {
      state.templates[payload.key] = payload.templates;
      state._lastUsedKey = payload.key;
    },
    pushTemplate: (state, { payload }: PayloadAction<IHandBookTemplate>) => {
      state.templates[state._lastUsedKey] = {
        ...state.templates[state._lastUsedKey],
        count: state.templates[state._lastUsedKey].count + 1,
        results: state.templates[state._lastUsedKey].results.concat(payload),
      };
    },
    setTemplatesMore: (
      state,
      { payload }: PayloadAction<{ key: string; templates: IListResponseData<IHandBookTemplate> }>
    ) => {
      const { key, templates } = payload;
      state.templates[key] = {
        results: [...(state.templates[key]?.results ?? []), ...templates.results],
        count: templates.count,
      };
    },
    setTemplateSections: (
      state,
      { payload }: PayloadAction<{ key: string; templateSections: IListResponseData<IHandBookTemplateSection> }>
    ) => {
      state.templateSections[payload.key] = payload.templateSections;
    },
    setTemplateSectionsMore: (
      state,
      { payload }: PayloadAction<{ key: string; templateSections: IListResponseData<IHandBookTemplateSection> }>
    ) => {
      const { key, templateSections } = payload;
      state.templateSections[key] = {
        results: [...(state.templateSections[key]?.results ?? []), ...templateSections.results],
        count: templateSections.count,
      };
    },
    setTemplateExpenditures: (
      state,
      { payload }: PayloadAction<{ key: string; templateExpenditures: IListResponseData<IHandBookTemplateExpenditure> }>
    ) => {
      state.templateExpenditures[payload.key] = payload.templateExpenditures;
      state._lastUsedKeyTemplateExpenditures = payload.key;
    },
    setTemplateExpendituresMore: (
      state,
      { payload }: PayloadAction<{ key: string; templateExpenditures: IListResponseData<IHandBookTemplateExpenditure> }>
    ) => {
      const { key, templateExpenditures } = payload;
      state.templateExpenditures[key] = {
        results: [...(state.templateExpenditures[key]?.results ?? []), ...templateExpenditures.results],
        count: templateExpenditures.count,
      };
    },
    pushTemplateExpenditures: (state, { payload }: PayloadAction<IHandBookTemplateExpenditure[]>) => {
      state.templateExpenditures[state._lastUsedKeyTemplateExpenditures] = {
        ...state.templateExpenditures[state._lastUsedKeyTemplateExpenditures],
        count: state.templateExpenditures[state._lastUsedKeyTemplateExpenditures].count + payload.length,
        results: state.templateExpenditures[state._lastUsedKeyTemplateExpenditures].results.concat(payload),
      };
    },
    setAggregate: (state, { payload }: PayloadAction<IHandBookAggregate>) => {
      state.handbookAggregate = payload;
    },
    setSegments: (state, { payload }: PayloadAction<IHandBookSegment[]>) => {
      state.segments = payload;
    },
    setAllSegments: (state, { payload }: PayloadAction<IHandBookSegment[]>) => {
      state.allSegments = payload;
    },
    pushSegments: (state, { payload }: PayloadAction<IHandBookSegment[]>) => {
      state.segments = state.segments.concat(payload);
    },
    setTemplateViewFilter: (state, { payload }: PayloadAction<Partial<HandBookTemplateViewFilters>>) => {
      state.templateViewFilters = { ...state.templateViewFilters, ...payload };
    },
  },
});

export const handBookActions = handBookSlice.actions;

export const handBookReducer = handBookSlice.reducer;
